import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const NAV = {
  en: { home: 'Home', about: 'About Us', services: 'Services', pricing: 'Pricing', security: 'Security', blog: 'Blog', contact: 'Contact', login: 'Sign in', cta: "Let's connect" },
  es: { home: 'Inicio', about: 'Nosotros', services: 'Servicios', pricing: 'Precios', security: 'Seguridad', blog: 'Blog', contact: 'Contacto', login: 'Iniciar sesión', cta: 'Conversemos' },
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang } = useLanguage();
  const t = NAV[lang] || NAV.en;
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: '/', label: t.home },
    { to: '/services', label: t.services },
    { to: '/pricing', label: t.pricing },
    { to: '/security', label: t.security },
    { to: '/contact', label: t.contact },
  ];

  return (
    <>
    <header
      className="fixed top-0 left-0 right-0 z-40 transition-all"
      style={{
        background: scrolled ? 'rgba(35,35,35,0.85)' : 'transparent',
        backdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(184,145,92,0.18)' : '1px solid transparent',
      }}
    >
      <div className="container-x h-[88px] flex items-center justify-between gap-6">
        <Link to="/" className="flex-shrink-0"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) => `px-5 py-2 text-[10.4px] uppercase tracking-[0.22em] font-medium transition-colors flex items-center gap-1 ${isActive ? 'text-[#FAF6EF]' : 'text-[#C6C4CF] hover:text-[#FAF6EF]'}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <a href="https://app.clicksyhub.com" target="_blank" rel="noreferrer" className="text-[10.4px] uppercase tracking-[0.22em] text-[#C6C4CF] hover:text-[#F5F137] inline-flex items-center gap-1">{t.login} <ArrowUpRight size={11} /></a>
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="btn-icon-only ml-2"><Menu size={18} /></button>
        </div>
        <button className="lg:hidden btn-icon-only" onClick={() => setOpen(true)} aria-label="Open menu"><Menu size={18} /></button>
      </div>
    </header>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1A1A1A] flex flex-col overflow-y-auto">
          <div className="container-x w-full h-[88px] flex items-center justify-end flex-shrink-0">
            <button onClick={() => setOpen(false)} className="btn-icon-only" aria-label="Close"><X size={18} /></button>
          </div>
          <nav className="flex flex-col gap-1 mt-8 max-w-3xl mx-auto w-full px-6 lg:px-0">
            {links.map((l, i) => (
              <Link key={l.to} to={l.to} className="group flex items-center justify-between py-5 border-b border-[#4F5758]">
                <span className="flex items-center gap-6">
                  <span className="text-xs text-[#F5F137] tracking-[0.22em] w-8">0{i+1}</span>
                  <span className="heading-display text-4xl lg:text-6xl group-hover:text-[#F5F137] transition-colors">{l.label}</span>
                </span>
                <ArrowUpRight size={28} className="text-[#C6C4CF] group-hover:text-[#F5F137] group-hover:rotate-45 transition-all" />
              </Link>
            ))}
          </nav>
          <div className="container-x w-full mt-auto flex items-center justify-between py-8">
            <LanguageSwitcher />
            <Link to="/contact" className="btn-outline"><span>{t.cta}</span><span className="arrow"><ArrowUpRight size={14} /></span></Link>
          </div>
        </div>
      )}
    </>
  );
}
